import { Column, Section } from '../models';
import { Data } from '../models/data';
import { CatalogSectionExtensionFrame } from './framework';

/**
 * Given a map of  { number: Partial<T> or C => Partial<T> } return partial
 * either the T value object or result value of given function of type C => T
 *
 * @param dataSource
 * @param key {number} sectionId, columnId
 * @param context
 */
export const overrideCatalog = <T, C>(
  dataSource: CatalogSectionExtensionFrame<T, C>,
  key: number,
  context: C
): Partial<T> => {
  const extension = dataSource[key];
  return typeof extension === 'function' ? extension(context) : extension;
};

/**
 * Executes overrideCatalog with given ExtensionFramework instance and given context to
 * extend T type (Section | Column) for a given element
 * @param extensionFw
 * @param context
 * @param key
 */
export function MakeCatalogExtender<T, C>(extensionFw: CatalogSectionExtensionFrame<T, C>, context: C, key: string) {
  return <X extends T>(targets: X[]): T[] =>
    targets.map(target => {
      const extension: Partial<T> = overrideCatalog(extensionFw, target[key], context) || {};
      return Object.assign({}, target, extension);
    });
}

/**
 * Extends Column attributes with given CatalogSectionExtensionFrame and C Context
 * @param columns
 * @param extensionFw
 * @param context
 *
 * @example
 * const columns = [{ columnId: 11 }]
 * const context = {userId: 1}
 * const extensionFw = { 11: { extended: cxt => ctx.userId == 5 ? true : false } }
 * extendColumns(columns, extensionFw, context)
 * // results [{ columnId: 11, extended: false }]
 */
export function extendColumns<R extends Column = Column, C = Data>(
  columns: Column[],
  extensionFw: CatalogSectionExtensionFrame<R, C>,
  context: C
): R[] {
  const columnsExtender: (targets: Column[]) => R[] = MakeCatalogExtender<R, C>(extensionFw, context, 'columnId');
  return columnsExtender(columns);
}

/**
 * Extends Section attributes with given CatalogSectionExtensionFrame and C Context
 * @param sections
 * @param extensionFw
 * @param context
 *
 * @example
 * const sections = [{sectionId:1, title: 'x', columns: []}]
 * const context = {userId: 1}
 * const extensionFw = { 1: { extended:true } }
 * extendColumns(section, extensionFw, context)
 * // results [{ sectionId: 1, title: 'x', columns: [], extended:true}]
 */
export function extendSections<S extends Section = Section, C = Data>(
  sections: Section[],
  extensionFw: CatalogSectionExtensionFrame<S, C>,
  context: C
): S[] {
  const sectionExtender: (targets: Section[]) => S[] = MakeCatalogExtender<S, C>(extensionFw, context, 'sectionId');
  return sectionExtender(sections);
}
